import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNotificationModal } from '../../contexts/NotificationModalContext';
import CreatorBadge from '../CreatorBadge';
import DesignationIcon from '../DesignationIcon';
import api from '../../services/api';

function ProfileDrawer({
  isOpen,
  onClose,
  onFollowersClick,
  onFriendsClick,
  listCount,
  summaryStats,
}) {
  const { user, logout, updateUserData } = useAuth();
  const { isPremium, isCreator } = useSubscription();
  const { showAlert, showToast } = useNotificationModal();
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const getUserId = () => user?.id || user?._id || user?.uid || user?.userId;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showAlert('Please select an image file', { variant: 'warning' });
      return;
    }

    if (file.size > 500 * 1024) {
      showAlert('Image must be less than 500KB', { variant: 'warning' });
      return;
    }

    setUploadingPhoto(true);

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const userId = getUserId();
        const response = await api.updateUser(userId, { profilePicture: reader.result });
        if (response.data) {
          updateUserData(response.data);
          showToast('Profile photo updated!');
        }
      } catch (error) {
        console.error('Failed to update profile photo:', error);
        showAlert('Failed to update profile photo', { variant: 'error' });
      } finally {
        setUploadingPhoto(false);
        if (avatarInputRef.current) {
          avatarInputRef.current.value = '';
        }
      }
    };
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="drawer-overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="profile-drawer" role="dialog" aria-modal="true" aria-labelledby="profile-drawer-title">
        <h2 id="profile-drawer-title" className="sr-only">Your Profile</h2>
        <div className="drawer-header">
          <button
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close profile"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="drawer-content">
          <div className="profile-section-drawer">
            <input
              type="file"
              ref={avatarInputRef}
              onChange={handleAvatarChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <div className={`profile-avatar-container ${isCreator ? 'avatar-with-badge' : ''}`}>
              <div
                className={`profile-avatar-wrapper ${uploadingPhoto ? 'uploading' : ''}`}
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
                aria-label="Change profile photo"
                onKeyDown={(e) => e.key === 'Enter' && handleAvatarClick()}
              >
                <div className={`profile-avatar ${isPremium ? 'premium-avatar' : ''}`}>
                  {(user.profilePicture || user.photoURL) ? (
                    <img src={user.profilePicture || user.photoURL} alt="Profile" className="profile-photo" />
                  ) : (
                    (user.displayName || user.name || user.username || 'U').charAt(0).toUpperCase()
                  )}
                </div>
                <div className="avatar-edit-overlay">
                  {uploadingPhoto ? (
                    <div className="avatar-spinner" />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  )}
                </div>
              </div>
              {isCreator && <CreatorBadge size="medium" />}
            </div>
            <h3 className="profile-name">{user.displayName || user.name || user.username}</h3>
            {user.username && <p className="profile-handle">@{user.username}</p>}
            {user.bio && <p className="profile-bio">{user.bio}</p>}
            {user.bioLink?.url && (
              <a
                href={user.bioLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="profile-bio-link"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <span>{user.bioLink.title || user.bioLink.url}</span>
              </a>
            )}
          </div>

          <div className="profile-stats">
            <div className="stat stat-non-clickable">
              <div className="stat-value">{listCount}</div>
              <div className="stat-label">{listCount === 1 ? 'List' : 'Lists'}</div>
            </div>
            <div className="stat stat-clickable" onClick={onFollowersClick}>
              <div className="stat-value">{user.followersCount || 0}</div>
              <div className="stat-label">{(user.followersCount || 0) === 1 ? 'Follower' : 'Followers'}</div>
            </div>
            <div className="stat stat-clickable" onClick={onFriendsClick}>
              <div className="stat-value">{user.friendsCount || 0}</div>
              <div className="stat-label">{(user.friendsCount || 0) === 1 ? 'Friend' : 'Friends'}</div>
            </div>
          </div>

          {summaryStats.totalPlaces > 0 && (
            <div className="places-summary">
              <div className="places-summary-header">
                <span className="places-summary-total">{summaryStats.totalPlaces.toLocaleString()} places saved</span>
                <span className="places-summary-percent">{summaryStats.percentVisited}% visited</span>
              </div>
              <div className="places-summary-badges">
                <span className="places-badge" title="Gem">
                  <span className="places-badge-icon">
                    <DesignationIcon designation="gem" size={14} />
                  </span>
                  <span className="places-badge-count">{summaryStats.gem.toLocaleString()}</span>
                </span>
                <span className="places-badge" title="Highly Recommended">
                  <span className="places-badge-icon">
                    <DesignationIcon designation="highly-recommended" size={14} />
                  </span>
                  <span className="places-badge-count">{summaryStats.highlyRecommended.toLocaleString()}</span>
                </span>
                <span className="places-badge" title="Visited">
                  <span className="places-badge-icon">
                    <DesignationIcon designation="visited" size={14} />
                  </span>
                  <span className="places-badge-count">{summaryStats.visited.toLocaleString()}</span>
                </span>
                <span className="places-badge" title="Avoid">
                  <span className="places-badge-icon">
                    <DesignationIcon designation="avoid" size={14} />
                  </span>
                  <span className="places-badge-count">{summaryStats.avoid.toLocaleString()}</span>
                </span>
                <span className="places-badge" title="Want to Go">
                  <span className="places-badge-icon">
                    <DesignationIcon designation="want-to-go" size={14} />
                  </span>
                  <span className="places-badge-count">{summaryStats.wantToGo.toLocaleString()}</span>
                </span>
              </div>
            </div>
          )}

          <div className="profile-actions">
            <button className="btn-settings" onClick={() => { onClose(); navigate('/settings'); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Settings
            </button>
            <button className="btn-logout" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProfileDrawer;
